import React, { ReactNode, useCallback, useMemo } from 'react';
import { Map as ImmutableMap } from 'immutable';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

import { accountsSelectors } from 'companion-app-components/flux/accounts';
import { categoriesSelectors } from 'companion-app-components/flux/categories';
import { transactionsTypes, transactionsUtils } from 'companion-app-components/flux/transactions';
import { chartOfAccountsSelectors, chartOfAccountsTypes } from 'companion-app-components/flux/chart-of-accounts';
import { scheduledTransactionsSelectors } from 'companion-app-components/flux/scheduled-transactions';
import RootState from 'companion-app-components/utils/redux-store/rootState';

import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import AlarmOn from '@mui/icons-material/AlarmOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { getTransactionsByAccountId, isTransferAccountAvailable } from 'data/transactions/selectors';
import { formatAmount } from 'components/QCurrency/core';
import QTip from 'components/QuickenControls/QTip';
import QMenu from 'components/QMenu';
import useQData from 'components/QData/useQData';

import {
  getCalendarTxnType, getTxnStateInfo, isUnacceptedScheduledTxn,
  nextScheduledInstanceAfterDate, transactionEditsNotAllowed, isNextScheduledInstance,
} from 'data/transactions/utils';
import {
  APP_STRING_SPLIT,
  APP_STRING_NO_PAYEE,
} from 'utils/constants';

const schTxnsIsNextMenuOption = [
  { label: 'Enter this reminder', value: 'ACCEPT' },
  { label: 'Ignore this reminder', value: 'SKIP' },
  { label: 'Go to Bills', value: 'BILLS' },
];

const schTxnsMenuOption = [
  { label: 'Enter this and all previous overdue reminders for this Payee as Paid', value: 'ACCEPT' },
  { label: 'Ignore this and all previous overdue reminders for this Payee', value: 'SKIP' },
  { label: 'Go to Bills', value: 'BILLS' },
];

const schTxnsGrossPaycheckMenuOption = [
  { label: 'Go to Bills', value: 'BILLS' },
];

interface TableTxnRowProps {
  columns: Record<string, any>;
  classes: Record<string, string>;
  txn: transactionsTypes.CashFlowTransaction;
  onGoToRegister: (txn: transactionsTypes.CashFlowTransaction) => void;
  onGoToBills: (stModelId: string) => void;
}

interface MenuOption { label: string; value: string; }

const TableTxnRow: React.FC<TableTxnRowProps> = (props) => {
  const { columns, classes, txn, onGoToRegister, onGoToBills } = props;

  const incomeCOAIds = useSelector(categoriesSelectors.getIncomeCOAIds) as unknown as chartOfAccountsTypes.ChartOfAccount;
  const accountsById = useSelector(accountsSelectors.getAccountsById);
  const allAccountsById = useSelector(accountsSelectors.getAllAccounts) as ImmutableMap<string, { name: string }>;
  const transactionsByAccountId = useSelector((state) => getTransactionsByAccountId(state, undefined)) as unknown as ImmutableMap<string, transactionsTypes.CashFlowTransaction>;
  const accountTransactions = transactionsByAccountId?.get(txn.accountId ?? '');
  const scheduledTransactions = useSelector(scheduledTransactionsSelectors.getScheduledTransactions);
  const category = useSelector((state: RootState) =>
    txn.coa && chartOfAccountsSelectors.getCoaStringSelector(state, txn.coa, true));
  const isTransferTxnAccountAvailable = useSelector((state) => isTransferAccountAvailable(state, txn));

  const { qDataPerformTransactionAction } = useQData('calendarTxnModal_TxPage');

  const isTxnInCurrentAccountGroup = useMemo(
    () => accountsById && accountsById.some(({ id }) => id === txn.accountId),
    [accountsById, txn.accountId],
  );
  const defaultMenuOption = isTxnInCurrentAccountGroup ? [{ label: 'Go to Register', value: 'REGISTER' }] : [];
  const type = useMemo(() => getCalendarTxnType(txn, incomeCOAIds), [txn, incomeCOAIds]);
  const recurringStatus = useMemo(() => getTxnStateInfo(txn), [txn]);
  const isUnacceptedSchTxn = useMemo(() => isUnacceptedScheduledTxn(txn), [txn]);
  const nextInstance = useMemo(() => accountTransactions && txn.stModelId &&
    nextScheduledInstanceAfterDate(DateTime.now().toISODate() ?? '', accountTransactions as any, txn.stModelId),
  [accountTransactions, txn]);
  const isNext = useMemo(() => nextInstance && nextInstance.id === txn.id, [nextInstance, txn]);
  const isUpcoming = isNextScheduledInstance(txn, scheduledTransactions);

  let menuOptions : MenuOption[] | null = null;
  let reminderIconClass: string | null = null;
  let qtipTitle: string | null = null;

  const scheduledTransactionsById = useSelector(scheduledTransactionsSelectors.getScheduledTransactions);
  const stRecord = scheduledTransactionsById.find(
    (item) => item.id === txn.stModelId,
  );
  const isTypeGrossPaycheck = Boolean(stRecord?.type === 'GROSS_PAYCHECK');

  if (isTypeGrossPaycheck) {
    menuOptions = schTxnsGrossPaycheckMenuOption;
  } else if (isUnacceptedSchTxn) {
    // if this scheduled instance contains transfers to unsynced accounts, then do not allow the user to act
    // @ts-expect-error: An argument for 'state' was not provided.
    menuOptions = !transactionEditsNotAllowed(txn) && isUpcoming ? schTxnsIsNextMenuOption : schTxnsMenuOption;
  }

  const { income, expense, overdue, normal } = transactionsTypes.calTxnLabelTypes;

  switch (type) {
    case income:
    case expense:
      if (recurringStatus.status === 'DUE') {
        qtipTitle = 'This reminder is due';
        reminderIconClass = classes.dueType;
      } else {
        if (isNext) {
          qtipTitle = 'This will be the next instance that becomes due';
        } else {
          qtipTitle = 'This reminder is not yet due';
          menuOptions = null;
        }
        reminderIconClass = classes.notDue;
      }
      break;
    case overdue:
      qtipTitle = 'This reminder is overdue';
      reminderIconClass = classes.overdueType;
      break;
    case normal:
    default:
      reminderIconClass = null;
  }

  if (reminderIconClass && category === '[Unsynced Account]') {
    menuOptions = schTxnsGrossPaycheckMenuOption;
  }

  const onMenuChange = useCallback((action: string) => {
    if (action === 'BILLS') {
      // @ts-expect-error: stModelId may be null but onGoToBills expects string
      onGoToBills(txn.stModelId);
      return;
    }
    if (action === 'REGISTER') {
      onGoToRegister(txn);
      return;
    }

    const actionObj = {
      id: txn.id,
      action,
      overdueAction: action,
      instanceDate: txn.stDueOn,
    };
    qDataPerformTransactionAction(actionObj);
  }, [onGoToBills, onGoToRegister, txn, qDataPerformTransactionAction]);

  let categoryName = category;
  let showOptionsCell = Boolean(defaultMenuOption?.length);
  if (transactionsUtils.isTransferTxn(txn)) {
    if (!isTransferTxnAccountAvailable) {
      categoryName = '[Transfer]';
    }
    if (transactionsUtils.isSplitTxn(txn)) {
      const splitItems = txn?.split?.items ?? [];
      const coaIds = (splitItems as any[])
        .map((split) => split?.coa)
        .filter((coa: chartOfAccountsTypes.ChartOfAccountProps) => !!coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT)
        .map((coa: chartOfAccountsTypes.ChartOfAccountProps) => coa.id!);
      const isTransferAccountPresent = coaIds.some((id) => Boolean(accountsById.get(id)));
      if (!isTransferAccountPresent) {
        showOptionsCell = false;
      }
    } else {
      const isTransferAccountPresent = accountsById.get(txn.coa?.id as string);
      if (!isTransferAccountPresent) {
        showOptionsCell = false;
      }
    }
  }

  return (
    <TableRow className={classes.tableBodyRow}>
      {Object.keys(columns).map((key) => {
        let cell: ReactNode | null = null;

        switch (key) {
          case 'reminderCol':
            cell = (
              <div className={classes.reminderColContainer}>
                {reminderIconClass && (
                  <QTip
                    title={qtipTitle}
                    wrapId={`qtip-${txn.id}-recurring`}
                  >
                    <AlarmOn className={classNames(classes.reminderIcon, reminderIconClass)} />
                  </QTip>
                )}
              </div>
            );
            break;
          case 'payeeCol':
            cell = <div className={classes.payeeColContainer}>{txn.payee || APP_STRING_NO_PAYEE}</div>;
            break;
          case 'accountCol':
            cell = <div className={classes.accountColContainer}>{allAccountsById?.get(txn.accountId ?? '')?.name}</div>;
            break;
          case 'categoryCol':
            cell = (
              <div className={classes.categoryColContainer}>
                {txn.split ? APP_STRING_SPLIT : categoryName}
              </div>
            );
            break;
          case 'amountCol':
            cell = <div className={classes.amountColContainer}>{formatAmount(txn.amount)}</div>;
            break;
          case 'optionsCol': {
            const customTrigger = (
              <ExpandMoreIcon className={classes.expandIcon} />
            );
            cell = showOptionsCell ? (
              <div className={classes.optionsColContainer}>
                <QMenu
                  customTrigger={customTrigger}
                  name={`qmenu-${txn.id}-recurring`}
                  title={qtipTitle}
                  menuIconButtonSize="small"
                  options={menuOptions || defaultMenuOption}
                  onChange={onMenuChange}
                  customTriggerClass={classes.expandIconRoot}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                />
              </div>
            ) : null;
          }
            break;
          default: break;
        }

        return (
          <TableCell key={`cell-${txn.id}-${key}`} className={classNames(classes.tableBodyCell, classes?.[key])}>
            {cell}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default React.memo(TableTxnRow);
